import React from 'react';
import {
  AUTH_SUGGESTIONS,
  PAYLOAD_FORMAT_OPTIONS,
  PROVIDER_LOCATION_LABEL,
  PROVIDER_LOCATION_PLACEHOLDER,
  PROVIDER_OPTIONS,
  defaultPayloadFormat,
} from '../aiModelConstants';
import { Field, inputClass } from '../aiModelUi';
import { AiModelHttpMethod, AiModelPayloadFormat, AiModelProvider } from '../aiModelTypes';
import { StepProps } from './stepTypes';

const SourceConnectionStep: React.FC<StepProps> = ({ form, isLight, errors, patch }) => {
  const locationLabel = PROVIDER_LOCATION_LABEL[form.provider];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Provider" isLight={isLight}>
          <select
            value={form.provider}
            onChange={(e) => {
              const provider = e.target.value as AiModelProvider;
              patch({
                provider,
                payloadFormat: defaultPayloadFormat(provider),
              });
            }}
            className={inputClass(isLight)}
          >
            {PROVIDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Model / Endpoint ID" error={errors.endpointId} isLight={isLight}>
          <input
            value={form.endpointId}
            onChange={(e) => patch({ endpointId: e.target.value })}
            placeholder="fraud-detection-prod"
            className={`${inputClass(isLight, Boolean(errors.endpointId))} font-mono`}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label={locationLabel} error={errors.location} isLight={isLight}>
          <input
            value={form.location}
            onChange={(e) => patch({ location: e.target.value })}
            placeholder={PROVIDER_LOCATION_PLACEHOLDER[form.provider]}
            className={inputClass(isLight, Boolean(errors.location))}
          />
        </Field>
        <Field
          label="Auth display name"
          hint="Optional label shown on the card — not a secret"
          isLight={isLight}
        >
          <input
            list="ai-model-auth-suggestions"
            value={form.authLabel}
            onChange={(e) => patch({ authLabel: e.target.value })}
            placeholder="Production Hugging Face token"
            className={inputClass(isLight)}
          />
          <datalist id="ai-model-auth-suggestions">
            {AUTH_SUGGESTIONS.map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>
        </Field>
      </div>

      <ProviderCredentials form={form} isLight={isLight} errors={errors} patch={patch} />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="HTTP method" isLight={isLight}>
          <select
            value={form.httpMethod}
            onChange={(e) => patch({ httpMethod: e.target.value as AiModelHttpMethod })}
            className={inputClass(isLight)}
          >
            <option value="POST">POST</option>
            <option value="GET">GET</option>
          </select>
        </Field>
        <Field label="Content type" isLight={isLight}>
          <input
            value={form.contentType}
            onChange={(e) => patch({ contentType: e.target.value })}
            className={inputClass(isLight)}
          />
        </Field>
        <Field label="Payload format" isLight={isLight}>
          <select
            value={form.payloadFormat}
            onChange={(e) => patch({ payloadFormat: e.target.value as AiModelPayloadFormat })}
            className={inputClass(isLight)}
          >
            {PAYLOAD_FORMAT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Timeout (ms)" error={errors.timeoutMs} isLight={isLight}>
          <input
            type="number"
            min={1}
            value={form.timeoutMs}
            onChange={(e) => patch({ timeoutMs: Number(e.target.value) || 0 })}
            className={inputClass(isLight, Boolean(errors.timeoutMs))}
          />
        </Field>
      </div>
      <Field label="Retries" hint="Used by workflow invoke later" isLight={isLight}>
        <input
          type="number"
          min={0}
          max={5}
          value={form.retryCount}
          onChange={(e) => patch({ retryCount: Number(e.target.value) || 0 })}
          className={`${inputClass(isLight)} max-w-[8rem]`}
        />
      </Field>
    </div>
  );
};

const ProviderCredentials: React.FC<StepProps> = ({ form, isLight, errors, patch }) => {
  const creds = form.credentials;
  const hints = form.credentialHints;
  const setCred = (key: keyof typeof creds, value: string) => {
    patch({ credentials: { ...creds, [key]: value } });
  };

  if (form.provider === 'huggingface') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Hugging Face token" error={errors.hfToken} hint={hints.hfToken.set ? `Stored ${hints.hfToken.hint} — leave blank to keep` : 'Write access token for Inference'} isLight={isLight}>
          <input type="password" autoComplete="off" value={creds.hfToken} onChange={(e) => setCred('hfToken', e.target.value)} className={inputClass(isLight, Boolean(errors.hfToken))} />
        </Field>
        <Field label="Dedicated endpoint URL" hint={hints.endpointUrl.set ? `Stored ${hints.endpointUrl.hint}` : 'Optional Inference Endpoint URL'} isLight={isLight}>
          <input value={creds.endpointUrl} onChange={(e) => setCred('endpointUrl', e.target.value)} placeholder="https://xyz.aws.endpoints.huggingface.cloud" className={inputClass(isLight)} />
        </Field>
      </div>
    );
  }

  if (form.provider === 'azure_ml') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Scoring URI" error={errors.scoringUri} hint={hints.scoringUri.set ? `Stored ${hints.scoringUri.hint} — leave blank to keep` : 'https://{endpoint}.region.inference.ml.azure.com/score'} isLight={isLight}>
          <input value={creds.scoringUri} onChange={(e) => setCred('scoringUri', e.target.value)} className={inputClass(isLight, Boolean(errors.scoringUri))} />
        </Field>
        <Field label="Azure API key" error={errors.apiKey} hint={hints.apiKey.set ? `Stored ${hints.apiKey.hint} — leave blank to keep` : undefined} isLight={isLight}>
          <input type="password" autoComplete="off" value={creds.apiKey} onChange={(e) => setCred('apiKey', e.target.value)} className={inputClass(isLight, Boolean(errors.apiKey))} />
        </Field>
        <Field label="Deployment name" hint="Optional azureml-model-deployment header" isLight={isLight}>
          <input value={creds.deploymentName} onChange={(e) => setCred('deploymentName', e.target.value)} className={inputClass(isLight)} />
        </Field>
      </div>
    );
  }

  if (form.provider === 'sagemaker') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="AWS access key ID" error={errors.awsAccessKeyId} hint={hints.awsAccessKeyId.set ? `Stored ${hints.awsAccessKeyId.hint} — leave blank to keep` : undefined} isLight={isLight}>
          <input value={creds.awsAccessKeyId} onChange={(e) => setCred('awsAccessKeyId', e.target.value)} className={inputClass(isLight, Boolean(errors.awsAccessKeyId))} />
        </Field>
        <Field label="AWS secret access key" error={errors.awsSecretAccessKey} hint={hints.awsSecretAccessKey.set ? `Stored ${hints.awsSecretAccessKey.hint} — leave blank to keep` : undefined} isLight={isLight}>
          <input type="password" autoComplete="off" value={creds.awsSecretAccessKey} onChange={(e) => setCred('awsSecretAccessKey', e.target.value)} className={inputClass(isLight, Boolean(errors.awsSecretAccessKey))} />
        </Field>
        <Field label="AWS session token" hint="Optional for temporary credentials" isLight={isLight}>
          <input type="password" autoComplete="off" value={creds.awsSessionToken} onChange={(e) => setCred('awsSessionToken', e.target.value)} className={inputClass(isLight)} />
        </Field>
      </div>
    );
  }

  if (form.provider === 'databricks') {
    return (
      <Field label="Databricks personal access token" error={errors.databricksToken} hint={hints.databricksToken.set ? `Stored ${hints.databricksToken.hint} — leave blank to keep` : undefined} isLight={isLight}>
        <input type="password" autoComplete="off" value={creds.databricksToken} onChange={(e) => setCred('databricksToken', e.target.value)} className={inputClass(isLight, Boolean(errors.databricksToken))} />
      </Field>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Bearer token" error={errors.bearerToken} hint={hints.bearerToken.set ? `Stored ${hints.bearerToken.hint} — leave blank to keep` : 'Used as Authorization: Bearer'} isLight={isLight}>
        <input type="password" autoComplete="off" value={creds.bearerToken} onChange={(e) => setCred('bearerToken', e.target.value)} className={inputClass(isLight, Boolean(errors.bearerToken))} />
      </Field>
      <Field label="API key" hint={hints.apiKey.set ? `Stored ${hints.apiKey.hint} — leave blank to keep` : 'Used if bearer token is empty'} isLight={isLight}>
        <input type="password" autoComplete="off" value={creds.apiKey} onChange={(e) => setCred('apiKey', e.target.value)} className={inputClass(isLight)} />
      </Field>
    </div>
  );
};

export default SourceConnectionStep;
